import type { Payout } from '../types/payout';

/**
 * Filter payouts by type(s)
 */
export const filterByType = (payouts: Payout[], ...types: Payout['type'][]): Payout[] => {
  return payouts.filter((p) => types.includes(p.type));
};

/**
 * Filter out payouts by type(s)
 */
export const filterOutType = (payouts: Payout[], ...types: Payout['type'][]): Payout[] => {
  return payouts.filter((p) => !types.includes(p.type));
};

/**
 * Filter payouts that are acquisitions (increase holdings)
 */
export const filterAcquisitions = (payouts: Payout[]): Payout[] => {
  return filterByType(payouts, 'payout', 'achat', 'stacking', 'swap');
};

/**
 * Filter payouts that are disposals (decrease holdings)
 */
export const filterDisposals = (payouts: Payout[]): Payout[] => {
  return filterByType(payouts, 'vente', 'swap');
};

/**
 * Filter out purchases (achats) - used when calculating mining-specific values
 */
export const filterOutPurchases = (payouts: Payout[]): Payout[] => {
  return filterOutType(payouts, 'achat');
};

/**
 * Filter out disposals (vente and swap) - used for holdings calculations
 */
export const filterOutDisposals = (payouts: Payout[]): Payout[] => {
  return filterOutType(payouts, 'vente', 'swap');
};

/**
 * Filter payouts within a date range
 */
export const filterByDateRange = (
  payouts: Payout[],
  startDate: Date,
  endDate: Date
): Payout[] => {
  return payouts.filter((p) => {
    const payoutDate = new Date(p.date);
    return payoutDate >= startDate && payoutDate <= endDate;
  });
};

/**
 * Filter payouts for a specific month and year
 */
export const filterByMonth = (payouts: Payout[], year: number, month: number): Payout[] => {
  return payouts.filter((p) => {
    const payoutDate = new Date(p.date);
    return payoutDate.getFullYear() === year && payoutDate.getMonth() === month;
  });
};

/**
 * Filter payouts for a specific year
 */
export const filterByYear = (payouts: Payout[], year: number): Payout[] => {
  return payouts.filter((p) => {
    const payoutDate = new Date(p.date);
    return payoutDate.getFullYear() === year;
  });
};

/**
 * Filter payouts for current month
 */
export const filterByCurrentMonth = (payouts: Payout[]): Payout[] => {
  const now = new Date();
  return filterByMonth(payouts, now.getFullYear(), now.getMonth());
};

/**
 * Filter payouts for current year
 */
export const filterByCurrentYear = (payouts: Payout[]): Payout[] => {
  const now = new Date();
  return filterByYear(payouts, now.getFullYear());
};

/**
 * Filter payouts that are mining-related (payout or stacking)
 */
export const filterMiningPayouts = (payouts: Payout[]): Payout[] => {
  return filterByType(payouts, 'payout', 'stacking');
};

/**
 * Filter payouts that are purchases
 */
export const filterPurchases = (payouts: Payout[]): Payout[] => {
  return filterByType(payouts, 'achat');
};

/**
 * Filter payouts that are for Waltio export (only payout type)
 */
export const filterForWaltioExport = (payouts: Payout[]): Payout[] => {
  return filterByType(payouts, 'payout');
};
